/* Bronson header: announcement slider, sticky nav, mega menus, mobile drawer, search drawer, localization */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 960px)');
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const lockScroll = (on) => document.documentElement.classList.toggle('bh-scroll-lock', on);

  /* ---------- Announcement bar ---------- */
  if (!customElements.get('bronson-announcement')) {
    customElements.define(
      'bronson-announcement',
      class extends HTMLElement {
        connectedCallback() {
          this.slides = [...this.querySelectorAll('[data-slide]')];
          if (this.slides.length < 2) return;
          this.index = 0;
          this.delay = parseInt(this.dataset.delay, 10) || 4000;
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
          this.addEventListener('mouseenter', () => this.stop());
          this.addEventListener('mouseleave', () => this.play());
          this.addEventListener('focusin', () => this.stop());
          this.addEventListener('focusout', () => this.play());
          this.play();
        }
        disconnectedCallback() {
          this.stop();
        }
        go(i, user) {
          const n = this.slides.length;
          const next = (i + n) % n;
          const dir = i > this.index ? 1 : -1;
          this.slides.forEach((s, idx) => {
            s.classList.remove('is-active', 'from-left', 'from-right');
            s.toggleAttribute('inert', idx !== next);
            s.setAttribute('aria-hidden', idx !== next);
          });
          const slide = this.slides[next];
          slide.classList.add(dir > 0 ? 'from-right' : 'from-left');
          void slide.offsetWidth;
          slide.classList.add('is-active');
          this.index = next;
          if (user) this.play();
        }
        play() {
          this.stop();
          if (this.dataset.autoplay !== 'true') return;
          this.timer = setInterval(() => this.go(this.index + 1), this.delay);
        }
        stop() {
          clearInterval(this.timer);
        }
      }
    );
  }

  /* ---------- Localization disclosure ---------- */
  if (!customElements.get('bronson-localization')) {
    customElements.define(
      'bronson-localization',
      class extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-disclosure-toggle]');
          this.list = this.querySelector('[data-disclosure-list]');
          this.input = this.querySelector('input[name="country_code"], input[name="locale_code"]');
          this.form = this.querySelector('form');
          this.toggle?.addEventListener('click', () => this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true'));
          this.querySelectorAll('[data-value]').forEach((opt) =>
            opt.addEventListener('click', (e) => {
              e.preventDefault();
              this.input.value = opt.dataset.value;
              this.form.submit();
            })
          );
          this.onDocClick = (e) => !this.contains(e.target) && this.setOpen(false);
          this.addEventListener('keyup', (e) => e.key === 'Escape' && (this.setOpen(false), this.toggle?.focus()));
          this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && this.setOpen(false));
        }
        setOpen(open) {
          if (!this.toggle) return;
          this.toggle.setAttribute('aria-expanded', open);
          this.list?.toggleAttribute('hidden', !open);
          this.classList.toggle('is-open', open);
          if (open) {
            document.addEventListener('click', this.onDocClick);
            this.list.scrollTop = 0;
          } else {
            document.removeEventListener('click', this.onDocClick);
          }
        }
      }
    );
  }

  /* ---------- Header ---------- */
  if (!customElements.get('bronson-header')) {
    customElements.define(
      'bronson-header',
      class extends HTMLElement {
        connectedCallback() {
          this.section = this.closest('.shopify-section');
          this.section?.classList.add('bh-section');
          this.section?.classList.toggle('bh-sticky', this.dataset.sticky === 'true');
          this.header = this.querySelector('[data-header]');
          this.main = this.querySelector('[data-main-row]');
          this.overlay = this.querySelector('[data-overlay]');
          this.drawer = this.querySelector('[data-drawer]');
          this.search = this.querySelector('[data-search]');

          this.measure = this.measure.bind(this);
          this.resizeObserver = new ResizeObserver(this.measure);
          this.resizeObserver.observe(this.header);
          window.addEventListener('scroll', () => this.onScroll(), { passive: true });
          DESKTOP.addEventListener('change', () => {
            this.closeAll();
            this.measure();
          });
          this.measure();
          this.onScroll();

          this.initMega();
          this.initDrawer();
          this.initSearch();

          this.overlay?.addEventListener('click', () => this.closeAll());
          document.addEventListener('keyup', (e) => {
            if (e.key !== 'Escape') return;
            if (this.search?.classList.contains('is-open')) return this.closeSearch(true);
            if (this.drawer?.classList.contains('is-open')) return this.closeDrawer(true);
            this.closeMega();
          });
        }

        /* sticky: the whole section sticks with a negative top so only the main row stays visible */
        measure() {
          if (!this.main || !this.section) return;
          const offset = this.main.offsetTop;
          this.section.style.setProperty('--bh-stick-offset', `${offset}px`);
          document.documentElement.style.setProperty('--bh-main-height', `${this.main.offsetHeight}px`);
          document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
          this.stickOffset = offset;
        }

        onScroll() {
          if (!this.section) return;
          const top = this.section.getBoundingClientRect().top;
          const stuck = this.dataset.sticky === 'true' && top <= -this.stickOffset + 0.5 && window.scrollY > 0;
          this.section.classList.toggle('bh-is-stuck', stuck);
          if (this.activeMega && this.activeMega.dataset.type === 'compact') this.positionCompact(this.activeMega);
        }

        overlayFor(kind) {
          if (!this.overlay) return;
          this.overlay.dataset.kind = kind || '';
          this.overlay.classList.toggle('is-visible', Boolean(kind));
        }

        closeAll() {
          this.closeMega();
          this.closeDrawer();
          this.closeSearch();
        }

        /* ----- mega menu ----- */
        initMega() {
          this.megaItems = [...this.querySelectorAll('[data-mega-parent]')];
          this.megaItems.forEach((item) => {
            const trigger = item.querySelector('[data-mega-trigger]');
            const panel = item.querySelector('[data-mega]');
            if (!panel) return;
            item.addEventListener('mouseenter', () => {
              if (!DESKTOP.matches) return;
              clearTimeout(this.megaCloseTimer);
              this.openMega(item);
            });
            item.addEventListener('mouseleave', () => {
              this.megaCloseTimer = setTimeout(() => this.closeMega(item), 120);
            });
            trigger.addEventListener('keydown', (e) => {
              if (e.key === 'ArrowDown' || e.key === ' ') {
                e.preventDefault();
                this.openMega(item);
                panel.querySelector(focusable)?.focus();
              }
            });
            trigger.addEventListener('click', (e) => {
              if (!('ontouchstart' in window) || !DESKTOP.matches) return;
              if (!item.classList.contains('is-open')) {
                e.preventDefault();
                this.openMega(item);
              }
            });
            item.addEventListener('focusout', (e) => {
              if (!item.contains(e.relatedTarget)) this.closeMega(item);
            });
          });
          this.querySelectorAll('[data-nav] > ul > li:not([data-mega-parent])').forEach((li) =>
            li.addEventListener('mouseenter', () => this.closeMega())
          );
        }

        openMega(item) {
          if (this.activeMega === item) return;
          if (this.activeMega) this.closeMega(this.activeMega, true);
          const panel = item.querySelector('[data-mega]');
          if (panel.dataset.type === 'compact') this.positionCompact(item);
          item.classList.add('is-open');
          item.querySelector('[data-mega-trigger]').setAttribute('aria-expanded', 'true');
          this.activeMega = item;
          this.overlayFor('mega');
        }

        closeMega(item = this.activeMega, swapping) {
          if (!item) return;
          item.classList.remove('is-open');
          item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
          if (item === this.activeMega) this.activeMega = null;
          if (!swapping && !this.activeMega) this.overlayFor(null);
        }

        positionCompact(item) {
          const panel = item.querySelector('[data-mega]');
          if (!panel) return;
          const headerRect = this.header.getBoundingClientRect();
          const mainRect = this.main.getBoundingClientRect();
          const trigger = item.querySelector('[data-mega-trigger]').getBoundingClientRect();
          const width = panel.offsetWidth;
          // keep the panel inside the page-width content area
          const minLeft = mainRect.left - headerRect.left;
          const maxLeft = mainRect.right - headerRect.left - width;
          let left = trigger.left - headerRect.left;
          left = Math.min(left, maxLeft);
          left = Math.max(left, minLeft);
          panel.style.setProperty('--bh-compact-left', `${left}px`);
        }

        /* ----- mobile drawer ----- */
        initDrawer() {
          this.menuButton = this.querySelector('[data-menu-button]');
          if (!this.drawer || !this.menuButton) return;
          this.menuButton.addEventListener('click', () =>
            this.drawer.classList.contains('is-open') ? this.closeDrawer(true) : this.openDrawer()
          );
          this.drawer.querySelectorAll('[data-drawer-parent]').forEach((link) =>
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const sub = link.nextElementSibling;
              this.drawerPush(sub, link);
            })
          );
          this.drawer.querySelectorAll('[data-drawer-back]').forEach((btn) =>
            btn.addEventListener('click', () => this.drawerPop())
          );
          this.drawerStack = [];
        }

        setPanelTop(el) {
          const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
          el.style.setProperty('--bh-panel-top', `${bottom}px`);
        }

        openDrawer() {
          this.closeSearch();
          this.closeMega();
          this.setPanelTop(this.drawer);
          this.drawer.classList.add('is-open');
          this.drawer.setAttribute('aria-hidden', 'false');
          this.menuButton.setAttribute('aria-expanded', 'true');
          this.menuButton.setAttribute('aria-label', this.menuButton.dataset.labelClose);
          this.classList.add('bh--drawer-open');
          lockScroll(true);
        }

        closeDrawer(returnFocus) {
          if (!this.drawer?.classList.contains('is-open')) return;
          this.drawer.classList.remove('is-open');
          this.drawer.setAttribute('aria-hidden', 'true');
          this.menuButton.setAttribute('aria-expanded', 'false');
          this.menuButton.setAttribute('aria-label', this.menuButton.dataset.labelOpen);
          this.classList.remove('bh--drawer-open');
          lockScroll(false);
          setTimeout(() => {
            while (this.drawerStack.length) this.drawerPop(true);
          }, 300);
          if (returnFocus) this.menuButton.focus();
        }

        drawerPush(sub, link) {
          if (!sub) return;
          const scroller = link.closest('[data-drawer-scroll]');
          this.drawerStack.push({ sub, link, scroller });
          if (scroller) {
            scroller.scrollTop = 0;
            scroller.classList.add('is-covered');
          }
          sub.scrollTop = 0;
          sub.classList.add('is-active');
          sub.setAttribute('aria-hidden', 'false');
          link.setAttribute('aria-expanded', 'true');
          this.drawer.dataset.depth = this.drawerStack.length;
          setTimeout(() => sub.querySelector('[data-drawer-back]')?.focus({ preventScroll: true }), 50);
        }

        drawerPop(silent) {
          const last = this.drawerStack.pop();
          if (!last) return;
          last.sub.classList.remove('is-active');
          last.sub.setAttribute('aria-hidden', 'true');
          last.link.setAttribute('aria-expanded', 'false');
          this.drawer.dataset.depth = this.drawerStack.length;
          if (!silent) last.link.focus({ preventScroll: true });
        }

        /* ----- search ----- */
        initSearch() {
          if (!this.search) return;
          this.searchButtons = [...this.querySelectorAll('[data-search-toggle]')];
          this.searchInput = this.search.querySelector('input[type="search"]');
          this.searchResults = this.search.querySelector('[data-search-results]');
          this.searchDefault = this.search.querySelector('[data-search-default]');
          this.searchClear = this.search.querySelector('[data-search-clear]');
          this.cache = new Map();

          this.searchButtons.forEach((btn) =>
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              this.search.classList.contains('is-open') ? this.closeSearch(true) : this.openSearch(btn);
            })
          );
          this.search.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', () => this.closeSearch(true)));
          this.searchClear?.addEventListener('click', () => {
            this.searchInput.value = '';
            this.renderSearch('');
            this.searchInput.focus();
          });
          let t;
          this.searchInput.addEventListener('input', () => {
            clearTimeout(t);
            t = setTimeout(() => this.renderSearch(this.searchInput.value.trim()), 250);
          });
        }

        openSearch(btn) {
          this.closeDrawer();
          this.closeMega();
          this.lastSearchButton = btn;
          this.setPanelTop(this.search);
          this.search.classList.add('is-open');
          this.search.setAttribute('aria-hidden', 'false');
          this.searchButtons.forEach((b) => b.setAttribute('aria-expanded', 'true'));
          this.classList.add('bh--search-open');
          this.overlayFor('search');
          lockScroll(true);
          setTimeout(() => this.searchInput.focus({ preventScroll: true }), 60);
        }

        closeSearch(returnFocus) {
          if (!this.search?.classList.contains('is-open')) return;
          this.search.classList.remove('is-open');
          this.search.setAttribute('aria-hidden', 'true');
          this.searchButtons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
          this.classList.remove('bh--search-open');
          if (this.overlay?.dataset.kind === 'search') this.overlayFor(null);
          lockScroll(false);
          if (returnFocus) this.lastSearchButton?.focus({ preventScroll: true });
        }

        async renderSearch(q) {
          this.searchClear?.toggleAttribute('hidden', !q);
          if (!q) {
            this.searchResults.innerHTML = '';
            this.searchResults.hidden = true;
            this.searchDefault.hidden = false;
            return;
          }
          const url = `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=query,product,collection&resources[limit]=4&resources[options][fields]=title,product_type,variants.title,vendor&section_id=bronson-predictive-search`;
          this.search.classList.add('is-loading');
          try {
            let html = this.cache.get(url);
            if (!html) {
              const res = await fetch(url);
              if (!res.ok) throw new Error(res.status);
              const text = await res.text();
              html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-bronson-predictive-search')?.innerHTML || '';
              this.cache.set(url, html);
            }
            if (this.searchInput.value.trim() !== q) return;
            this.searchResults.innerHTML = html;
            this.searchResults.hidden = false;
            this.searchDefault.hidden = true;
          } catch (e) {
            this.searchResults.hidden = true;
            this.searchDefault.hidden = false;
          } finally {
            this.search.classList.remove('is-loading');
          }
        }
      }
    );
  }
})();
